import { useContext, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  MessagesFeedProvider,
  useMessagesFeedState,
} from "./MessagesFeedContext";
import {
  NotificationsFeedProvider,
  useNotificationsFeedState,
} from "./NotificationsFeedContext";
import { UserDataContext } from "./Contexts";
import { emitPrivacySettingsRealtimeEvent } from "../components/privacy/privacyRealtimeEvents";
import { acquireSharedChatHubConnection } from "../../infrastructure/realtime/sharedChatHubConnection";
import {
  EVENT_REALTIME_EVENTS,
  EVENT_REALTIME_BROWSER_EVENT,
} from "../../infrastructure/realtime/eventRealtimeEvents";

const REALTIME_REFRESH_DELAY_MS = 350;
const MESSAGE_REALTIME_EVENTS = ["ReceiveMessage", "ReceiveReaction"];
const CHAT_STATE_REALTIME_EVENTS = [
  "ReceiveBlockChatUser",
  "ReceiveBlockChat",
  "ReceiveReportMessage",
  "ReceiveDeleteChat",
  "ReceiveArchiveChat",
];
const MESSAGE_NOTIFICATION_REALTIME_EVENTS = [
  "ReceiveMessageNotificationRead",
  "ReceiveMessageNotificationCount",
];

const APP_REALTIME_EVENT = "app:realtime-event";
const USER_CONNECTIONS_CHANGED_EVENT =
  "vivanomada:user-connections-changed";

const CONNECTION_REALTIME_EVENTS = new Set([
  "ReceiveUserFollow",
  "ReceiveFollowUser",
  "ReceiveUnfollowUser",
  "ReceiveUserUnfollow",
  "ReceiveFollowerChanged",
  "ReceiveFollowersChanged",
  "ReceiveConnectionChanged",
  "ReceiveConnectionsChanged",
  "ReceiveFollowRequest",
  "ReceiveFollowAccepted",
]);

// Eventos que otras pantallas necesitan en tiempo real, pero que no requieren
// abrir otra conexion SignalR ni refrescar el centro de notificaciones.
const PASS_THROUGH_REALTIME_EVENTS = [
  "ReceiveSavePublication",
  "ReceiveSavedPublication",
  "ReceivePublicationSaved",
  "ReceiveSavedPublicationsChanged",
  "ReceiveFavoritePublication",
  "ReceiveSaveRecommendation",
  "ReceiveSavedRecommendation",
  "ReceiveRecommendationSaved",
  "ReceiveSavedPlacesChanged",
  "ReceiveSavedPlace",
  "ReceiveSaveRecomendation",
  "ReceiveSavedRecomendation",
  "ReceiveRecomendationSaved",
  "ReceiveSavedRecommendationsChanged",
  "ReceiveSavedRecomendationsChanged",
  "ReceiveFavoriteRecommendation",
  "ReceiveFavoriteRecomendation",
];
const NOTIFICATION_REALTIME_EVENTS = [
  "ReceiveNewPost",
  "ReceiveNewForumPost",
  "ReceivePost",
  "ReceiveNewPublication",
  "ReceivePublication",
  "ReceiveEditPost",
  "ReceiveEditForumPost",
  "ReceiveEditedPost",
  "ReceiveUpdatePost",
  "ReceiveUpdatedPost",
  "ReceivePostEdited",
  "ReceivePublicationEdited",
  "ReceiveEditPublication",
  "ReceiveUpdatePublication",
  "ReceiveUpdatedPublication",
  "ReceiveReactionPost",
  "ReceiveVoteForumPost",
  "ReceivePostReaction",
  "ReceiveReactionPublication",
  "ReceivePublicationReaction",
  "ReceiveReportPost",
  "ReceiveReportForumPost",
  "ReceiveReportedPost",
  "ReceiveReportPublication",
  "ReceivePostReported",
  "ReceivePublicationReported",
  "ReceiveFavoritePost",
  "ReceiveSharePost",
  "ReceiveCommentPost",
  "ReceiveCommentForumPost",
  "ReceivePostComment",
  "ReceiveCommentPublication",
  "ReceivePublicationComment",
  "ReceiveReactionComment",
  "ReceiveReactionForumComment",
  "ReceiveEditComment",
  "ReceiveEditCommentPost",
  "ReceiveCommentEdit",
  "ReceiveUpdateComment",
  "ReceiveCommentUpdated",
  "ReceiveCommentEdited",
  "ReceiveEditedComment",
  "ReceiveEditComent",
  "ReceiveDeleteComment",
  "ReceiveDeleteCommentPost",
  "ReceiveCommentDelete",
  "ReceiveRemoveComment",
  "ReceiveCommentRemoved",
  "ReceiveDeletedComment",
  "ReceiveDeletePost",
  "ReceiveDeleteForumPost",
  "ReceiveDeletedPost",
  "ReceivePostDeleted",
  "ReceiveDeletePublication",
  "ReceivePublicationDeleted",
  "ReceiveHidePost",
  "ReceiveHiddenPost",
  "ReceiveHidePublication",
  "ReceivePostHidden",
  "ReceivePublicationHidden",
  "ReceiveSaveCoupon",
  "ReceiveSavedCoupon",
  "ReceiveCouponSaved",
  "ReceiveSavedCouponsChanged",
  "ReceiveFavoriteCoupon",
  "ReceiveUserFollow",
  "ReceiveFollowUser",
  "ReceiveUnfollowUser",
  "ReceiveUserUnfollow",
  "ReceiveFollowerChanged",
  "ReceiveFollowersChanged",
  "ReceiveConnectionChanged",
  "ReceiveConnectionsChanged",
  "ReceiveFollowRequest",
  "ReceiveFollowAccepted",
  "ReceiveNotification",
  "ReceiveNotificationRead",
  "ReceiveNotificationsViewed",
  "ReceiveNotifications",
  "ReceiveGeneralNotification",
];

const PRIVACY_SETTINGS_REALTIME_EVENTS = [
  "ReceivePrivacySettings",
  "ReceivePrivacySettingsUpdated",
  "ReceivePrivacyConfiguration",
  "ReceivePrivacyConfigurationUpdated",
  "ReceiveUpdatePrivacySettings",
  "ReceiveUpdatedPrivacySettings",
  "ReceiveUpdatePrivacyConfiguration",
  "ReceiveUpdatedPrivacyConfiguration",
];

const FEED_REALTIME_EVENTS = new Set([
  "ReceiveNewPost",
  "ReceiveNewForumPost",
  "ReceivePost",
  "ReceiveNewPublication",
  "ReceivePublication",
  "ReceiveEditPost",
  "ReceiveEditForumPost",
  "ReceiveEditedPost",
  "ReceiveUpdatePost",
  "ReceiveUpdatedPost",
  "ReceivePostEdited",
  "ReceivePublicationEdited",
  "ReceiveEditPublication",
  "ReceiveUpdatePublication",
  "ReceiveUpdatedPublication",
  "ReceiveReactionPost",
  "ReceiveVoteForumPost",
  "ReceivePostReaction",
  "ReceiveReactionPublication",
  "ReceivePublicationReaction",
  "ReceiveReportPost",
  "ReceiveReportForumPost",
  "ReceiveReportedPost",
  "ReceiveReportPublication",
  "ReceivePostReported",
  "ReceivePublicationReported",
  "ReceiveFavoritePost",
  "ReceiveSharePost",
  "ReceiveCommentPost",
  "ReceiveCommentForumPost",
  "ReceivePostComment",
  "ReceiveCommentPublication",
  "ReceivePublicationComment",
  "ReceiveReactionComment",
  "ReceiveReactionForumComment",
  "ReceiveEditComment",
  "ReceiveEditCommentPost",
  "ReceiveCommentEdit",
  "ReceiveUpdateComment",
  "ReceiveCommentUpdated",
  "ReceiveCommentEdited",
  "ReceiveEditedComment",
  "ReceiveEditComent",
  "ReceiveDeleteComment",
  "ReceiveDeleteCommentPost",
  "ReceiveCommentDelete",
  "ReceiveRemoveComment",
  "ReceiveCommentRemoved",
  "ReceiveDeletedComment",
  "ReceiveDeletePost",
  "ReceiveDeleteForumPost",
  "ReceiveDeletedPost",
  "ReceivePostDeleted",
  "ReceiveDeletePublication",
  "ReceivePublicationDeleted",
  "ReceiveHidePost",
  "ReceiveHiddenPost",
  "ReceiveHidePublication",
  "ReceivePostHidden",
  "ReceivePublicationHidden",
]);

const SAVED_COUPON_REALTIME_EVENTS = new Set([
  "ReceiveSaveCoupon",
  "ReceiveSavedCoupon",
  "ReceiveCouponSaved",
  "ReceiveSavedCouponsChanged",
  "ReceiveFavoriteCoupon",
]);

const isFeedRealtimeDebugEnabled = () =>
  localStorage.getItem("debugFeedRealtime") === "true";

const debugFeedRealtime = (...args) => {
  if (isFeedRealtimeDebugEnabled()) {
    console.log("[FeedRealtime]", ...args);
  }
};

const NotificationsCenterState = ({ children }) => {
  const { userData } = useContext(UserDataContext);
  const authToken = useSelector((state) => state.authToken?.token);
  const messagesFeed = useMessagesFeedState();
  const notificationsFeed = useNotificationsFeedState();
  const { refreshMessages, resetMessagesFeed } = messagesFeed;
  const { refreshNotifications, resetNotificationsFeed } = notificationsFeed;
  const refreshMessagesRef = useRef(refreshMessages);
  const refreshNotificationsRef = useRef(refreshNotifications);

  useEffect(() => {
    refreshMessagesRef.current = refreshMessages;
  }, [refreshMessages]);

  useEffect(() => {
    refreshNotificationsRef.current = refreshNotifications;
  }, [refreshNotifications]);

  useEffect(() => {
    if (!userData?.idUsuario) {
      resetMessagesFeed();
      resetNotificationsFeed();
      return;
    }

    const refreshCenter = async ({ forceNotifications = false } = {}) => {
      await Promise.all([
        refreshMessages({
          notifySidebar: true,
          source: "initial-load",
        }),
        refreshNotifications({ force: forceNotifications }),
      ]);
    };

    refreshCenter();

    const handleRefreshMessages = () => {
      refreshMessages({
        notifySidebar: false,
        source: "manual-badge-refresh",
      });
    };

    const handleRefreshNotifications = () => {
      refreshNotifications({ force: true });
    };

    const handleRefreshAll = () => {
      refreshCenter({ forceNotifications: true });
    };

    window.addEventListener(
      "messages:refresh-notifications",
      handleRefreshMessages,
    );
    window.addEventListener(
      "notifications:refresh-feed",
      handleRefreshNotifications,
    );
    window.addEventListener("notifications:refresh-all", handleRefreshAll);

    return () => {
      window.removeEventListener(
        "messages:refresh-notifications",
        handleRefreshMessages,
      );
      window.removeEventListener(
        "notifications:refresh-feed",
        handleRefreshNotifications,
      );
      window.removeEventListener("notifications:refresh-all", handleRefreshAll);
    };
  }, [
    refreshMessages,
    refreshNotifications,
    resetMessagesFeed,
    resetNotificationsFeed,
    userData?.idUsuario,
  ]);

  useEffect(() => {
    const realtimeToken = authToken || localStorage.getItem("token");
    if (!userData?.idUsuario || !realtimeToken) return;

    let isDisposed = false;
    let messageRefreshTimeout = null;
    let notificationRefreshTimeout = null;
    const messageHandlers = new Map();
    const connectionLease = acquireSharedChatHubConnection({
      userId: userData.idUsuario,
      accessTokenFactory: () =>
        authToken || localStorage.getItem("token") || "",
    });
    if (!connectionLease) return;

    const hubConnection = connectionLease.connection;

    const refreshMessagesFromRealtime = () => {
      window.clearTimeout(messageRefreshTimeout);
      messageRefreshTimeout = window.setTimeout(() => {
        refreshMessagesRef.current({
          notifySidebar: true,
          source: "signalr",
        });
      }, REALTIME_REFRESH_DELAY_MS);
    };

    const refreshNotificationsFromRealtime = () => {
      window.clearTimeout(notificationRefreshTimeout);
      notificationRefreshTimeout = window.setTimeout(async () => {
        await refreshNotificationsRef.current({ force: true });
        window.dispatchEvent(new CustomEvent("notifications:tab-refresh"));
      }, REALTIME_REFRESH_DELAY_MS);
    };

    const refreshCenterFromRealtime = () => {
      refreshMessagesFromRealtime();
      refreshNotificationsFromRealtime();
    };

    const dispatchAppRealtimeEvent = (eventName, args) => {
      window.dispatchEvent(
        new CustomEvent(APP_REALTIME_EVENT, {
          detail: { eventName, args },
        }),
      );
    };

    const dispatchConnectionsChanged = (eventName, args) => {
      if (!CONNECTION_REALTIME_EVENTS.has(eventName)) return;

      const payload = args.find(
        (arg) => arg && typeof arg === "object" && !Array.isArray(arg),
      );

      window.dispatchEvent(
        new CustomEvent(USER_CONNECTIONS_CHANGED_EVENT, {
          detail: {
            ...(payload || {}),
            eventName,
            args,
            realtime: true,
          },
        }),
      );
    };

    const startConnection = async () => {
      try {
        MESSAGE_REALTIME_EVENTS.forEach((eventName) => {
          const handler = (...args) => {
            refreshMessagesFromRealtime();
            if (eventName === "ReceiveReaction") {
              refreshNotificationsFromRealtime();
            }
            dispatchAppRealtimeEvent(eventName, args);
            window.dispatchEvent(
              new CustomEvent(
                eventName === "ReceiveMessage"
                  ? "chat:realtime-message"
                  : "chat:realtime-reaction",
                { detail: { args } },
              ),
            );
          };
          messageHandlers.set(eventName, handler);
          hubConnection.on(eventName, handler);
        });
        CHAT_STATE_REALTIME_EVENTS.forEach((eventName) => {
          const handler = (...args) => {
            refreshMessagesFromRealtime();
            dispatchAppRealtimeEvent(eventName, args);
            window.dispatchEvent(
              new CustomEvent("chat:realtime-state-change", {
                detail: { eventName, args },
              }),
            );
          };
          messageHandlers.set(eventName, handler);
          hubConnection.on(eventName, handler);
        });
        MESSAGE_NOTIFICATION_REALTIME_EVENTS.forEach((eventName) => {
          const handler = (...args) => {
            debugFeedRealtime("SignalR message notification event received", eventName, args);
            refreshMessagesFromRealtime();
            dispatchAppRealtimeEvent(eventName, args);
          };
          messageHandlers.set(eventName, handler);
          hubConnection.on(eventName, handler);
        });
        NOTIFICATION_REALTIME_EVENTS.forEach((eventName) => {
          const handler = (...args) => {
            debugFeedRealtime("SignalR event received", eventName, args);
            refreshNotificationsFromRealtime();
            dispatchAppRealtimeEvent(eventName, args);
            dispatchConnectionsChanged(eventName, args);
            if (FEED_REALTIME_EVENTS.has(eventName)) {
              debugFeedRealtime("Dispatching feed:realtime-event", eventName, args);
              window.dispatchEvent(
                new CustomEvent("feed:realtime-event", {
                  detail: { eventName, args },
                }),
              );
            }
            if (SAVED_COUPON_REALTIME_EVENTS.has(eventName)) {
              window.dispatchEvent(
                new CustomEvent("saved-coupon:realtime-event", {
                  detail: { eventName, args },
                }),
              );
            }
          };
          messageHandlers.set(eventName, handler);
          hubConnection.on(eventName, handler);
        });
        PASS_THROUGH_REALTIME_EVENTS.forEach((eventName) => {
          const handler = (...args) => {
            debugFeedRealtime("SignalR pass-through event received", eventName, args);
            dispatchAppRealtimeEvent(eventName, args);
          };
          messageHandlers.set(eventName, handler);
          hubConnection.on(eventName, handler);
        });
        // Eventos del modulo de Eventos. Viajan por esta misma conexion, asi que
        // lo unico que hace falta es escucharlos y repartirlos: el listado y el
        // detalle se suscriben al evento de navegador, no al hub.
        EVENT_REALTIME_EVENTS.forEach((eventName) => {
          const handler = (...args) => {
            debugFeedRealtime("SignalR evento recibido (eventos)", eventName, args);
            // Tambien refrescan la campana: estos movimientos generan
            // notificacion para el organizador y para quien asiste.
            refreshNotificationsFromRealtime();
            dispatchAppRealtimeEvent(eventName, args);
            window.dispatchEvent(
              new CustomEvent(EVENT_REALTIME_BROWSER_EVENT, {
                detail: { eventName, args },
              }),
            );
          };
          messageHandlers.set(eventName, handler);
          hubConnection.on(eventName, handler);
        });
        PRIVACY_SETTINGS_REALTIME_EVENTS.forEach((eventName) => {
          const handler = (...args) => {
            debugFeedRealtime("SignalR privacy event received", eventName, args);
            dispatchAppRealtimeEvent(eventName, args);
            emitPrivacySettingsRealtimeEvent({
              kind: "privacy-settings-updated",
              eventName,
              args,
            });
          };
          messageHandlers.set(eventName, handler);
          hubConnection.on(eventName, handler);
        });
        hubConnection.onreconnected(refreshCenterFromRealtime);

        await connectionLease.start();
        debugFeedRealtime("SignalR connected", {
          connectionId: hubConnection.connectionId,
          userId: userData.idUsuario,
        });

        if (isDisposed) return;
      } catch (error) {
        const wasStoppedDuringSetup =
          isDisposed ||
          error?.name === "AbortError" ||
          error?.message?.includes?.("stopped during negotiation");

        if (!wasStoppedDuringSetup) {
          console.error("Error connecting notifications SignalR", error);
          debugFeedRealtime("SignalR connection error", error);
        }
      }
    };

    startConnection();

    return () => {
      isDisposed = true;
      window.clearTimeout(messageRefreshTimeout);
      window.clearTimeout(notificationRefreshTimeout);
      MESSAGE_REALTIME_EVENTS.forEach((eventName) => {
        hubConnection.off(eventName, messageHandlers.get(eventName));
      });
      CHAT_STATE_REALTIME_EVENTS.forEach((eventName) => {
        hubConnection.off(eventName, messageHandlers.get(eventName));
      });
      MESSAGE_NOTIFICATION_REALTIME_EVENTS.forEach((eventName) => {
        hubConnection.off(eventName, messageHandlers.get(eventName));
      });
      NOTIFICATION_REALTIME_EVENTS.forEach((eventName) => {
        hubConnection.off(eventName, messageHandlers.get(eventName));
      });
      PASS_THROUGH_REALTIME_EVENTS.forEach((eventName) => {
        hubConnection.off(eventName, messageHandlers.get(eventName));
      });
      EVENT_REALTIME_EVENTS.forEach((eventName) => {
        hubConnection.off(eventName, messageHandlers.get(eventName));
      });
      PRIVACY_SETTINGS_REALTIME_EVENTS.forEach((eventName) => {
        hubConnection.off(eventName, messageHandlers.get(eventName));
      });
      connectionLease.release();
    };
  }, [authToken, userData?.idUsuario]);

  return (
    <MessagesFeedProvider value={messagesFeed}>
      <NotificationsFeedProvider value={notificationsFeed}>
        {children}
      </NotificationsFeedProvider>
    </MessagesFeedProvider>
  );
};

export const NotificationsCenterProvider = ({ children }) => {
  return <NotificationsCenterState>{children}</NotificationsCenterState>;
};
